import { createSlice } from '@reduxjs/toolkit';

/**
 * Participant Slice
 *
 * Manages state for participants taking online assessments:
 * - Code validation
 * - Participant registration
 * - Quiz attempt state
 * - Results display
 */

const initialState = {
  // Access code validation
  accessCode: '',
  linkCode: '', // Public link code from shareable URL
  isValidCode: false,
  isLinkCodeValidated: false, // Whether linkCode has been validated (two-step flow)
  assessmentInfo: null,
  creatorInstitute: null, // Creator's institute info

  // Institute search (kept for backwards compatibility but simplified)
  institutes: [],
  searchLoading: false,

  // Participant information
  participantInfo: {
    firstName: '',
    lastName: '',
    email: '',
    instituteType: null, // 'EXISTING' | 'NEW' | 'INDIVIDUAL'
    instituteId: null,
    instituteName: '',
    requestedInstituteName: '',
    instituteCity: '',
    instituteState: ''
  },

  // Quiz attempt state
  attempt: {
    attemptId: null,
    attemptNumber: 1,
    remainingAttempts: 0,
    assessmentName: '',
    duration: 0,
    totalQuestions: 0,
    startedAt: null,
    questions: [],
    currentQuestionIndex: 0,
    answers: {}, // { questionId: answerText }
    // Answers the SERVER has not confirmed. The text still shows on screen, but
    // nothing may treat these as stored: a failed auto-save used to write into
    // `answers` and look identical to a saved one, which silently cost students
    // marks (review 2026-09-12). Cleared only by a successful save.
    unsavedAnswers: {}, // { questionId: answerText }
    timeRemaining: 0,
    // Internal choice groups (e.g., "Attempt any 3 of 5")
    choiceGroups: [], // { groupId, label, totalQuestions, requiredCount, questionIds }
    // TEST_PREP sectional timer fields
    hasSectionalTimer: false,
    sectionSwitchingAllowed: true,
    currentSectionIndex: 0,
    sections: [], // { sectionCode, sectionName, timeLimit, timeRemainingSeconds, marking, isLocked, isActive }
    sectionProgress: []
  },

  // Question states for navigator (TEST_PREP)
  questionStates: {}, // { [questionId]: { visited, markedForReview } }

  // Quiz result
  result: {
    attemptId: null,
    assessmentName: '',
    participantName: '',
    score: null,
    status: '',
    submittedAt: null,
    timeSpentSeconds: 0,
    answers: [],
    showCorrectAnswers: false
  },

  // Flow state
  flow: {
    step: 'enter_code', // 'enter_code' | 'participant_info' | 'taking_quiz' | 'result'
    isSubmitting: false,
    isStarting: false,
    isSavingAnswer: false
  },

  // ============ Proctoring (per PRD-ONLINE-PROCTORING) ============
  // `enabled` mirrors `assessmentInfo.proctoring.enabled` once the config
  // is loaded; the per-signal flags below let the hook decide which
  // listeners to attach. Slot 1 only uses tabSwitch. The live counters
  // power the ProctoringPill the participant sees during the quiz.
  proctoring: {
    enabled: false,
    tabSwitch: false,
    webcam: false,
    gaze: false,
    liveCounts: {
      tabSwitchCount: 0,
      screenSwitchCount: 0,
      blurCount: 0,
      totalAwayMs: 0
    },
    // Last flush result from the backend — { accepted, rejected, reasons[] }.
    // Surfaced to the UI only when rejected > 0 so the silent-loss class
    // (PRD §11 contract test) cannot fail without the participant noticing.
    lastFlush: null
  },

  // Loading states
  loading: {
    validateCode: false,
    searchInstitutes: false,
    startAttempt: false,
    saveAnswer: false,
    submitQuiz: false,
    loadResult: false
  },

  // Error state
  error: null
};

const participantSlice = createSlice({
  name: 'participant',
  initialState,
  reducers: {
    // ============ Access Code ============
    setAccessCode: (state, action) => {
      state.accessCode = action.payload;
    },
    setLinkCode: (state, action) => {
      state.linkCode = action.payload;
    },
    setValidateCodeLoading: (state, action) => {
      state.loading.validateCode = action.payload;
    },
    setCodeValidated: (state, action) => {
      state.isValidCode = true;
      state.assessmentInfo = action.payload;
      state.creatorInstitute = action.payload.creatorInstitute || null;
      // Mirror proctoring config so the hook + pill can read from one place.
      const proctoringCfg = action.payload?.proctoring || {};
      state.proctoring.enabled = Boolean(proctoringCfg.enabled);
      state.proctoring.tabSwitch = Boolean(proctoringCfg.tabSwitch);
      state.proctoring.webcam = Boolean(proctoringCfg.webcam);
      state.proctoring.gaze = Boolean(proctoringCfg.gaze);
      state.loading.validateCode = false;
      state.flow.step = 'participant_info';
    },
    setLinkCodeValidated: (state, action) => {
      state.isLinkCodeValidated = true;
      state.assessmentInfo = action.payload;
      state.creatorInstitute = action.payload.creatorInstitute || null;
      const proctoringCfg = action.payload?.proctoring || {};
      state.proctoring.enabled = Boolean(proctoringCfg.enabled);
      state.proctoring.tabSwitch = Boolean(proctoringCfg.tabSwitch);
      state.proctoring.webcam = Boolean(proctoringCfg.webcam);
      state.proctoring.gaze = Boolean(proctoringCfg.gaze);
      state.loading.validateCode = false;
      state.flow.step = 'enter_code'; // Stay on code entry for accessCode input
    },
    setCodeInvalid: (state, action) => {
      state.isValidCode = false;
      state.assessmentInfo = null;
      state.error = action.payload;
      state.loading.validateCode = false;
    },

    // ============ Institute Search ============
    setSearchLoading: (state, action) => {
      state.loading.searchInstitutes = action.payload;
    },
    setInstitutes: (state, action) => {
      state.institutes = action.payload;
      state.loading.searchInstitutes = false;
    },
    clearInstitutes: (state) => {
      state.institutes = [];
    },

    // ============ Participant Info ============
    updateParticipantInfo: (state, action) => {
      state.participantInfo = { ...state.participantInfo, ...action.payload };
    },
    setParticipantInstituteType: (state, action) => {
      state.participantInfo.instituteType = action.payload;
      // Reset institute fields when type changes
      if (action.payload === 'INDIVIDUAL') {
        state.participantInfo.instituteId = null;
        state.participantInfo.instituteName = '';
      }
      // Clear request fields for simplified flow
      state.participantInfo.requestedInstituteName = '';
      state.participantInfo.instituteCity = '';
      state.participantInfo.instituteState = '';
    },
    setSelectedInstitute: (state, action) => {
      const { name, tenantDbName } = action.payload;
      state.participantInfo.instituteId = tenantDbName;
      state.participantInfo.instituteName = name;
    },

    // ============ Quiz Attempt ============
    setStartAttemptLoading: (state, action) => {
      state.loading.startAttempt = action.payload;
      state.flow.isStarting = action.payload;
    },
    setAttemptStarted: (state, action) => {
      const {
        attemptId, attemptNumber, remainingAttempts, assessmentName, duration,
        totalQuestions, startedAt, questions,
        // Internal choice fields
        choiceGroups,
        // TEST_PREP fields
        hasSectionalTimer, sectionSwitchingAllowed, currentSectionIndex, sections, sectionProgress
      } = action.payload;

      // Validate questions array
      const validQuestions = Array.isArray(questions) ? questions : [];

      state.attempt = {
        attemptId,
        attemptNumber,
        remainingAttempts,
        assessmentName,
        duration,
        totalQuestions: totalQuestions || validQuestions.length,
        startedAt,
        questions: validQuestions,
        currentQuestionIndex: 0,
        answers: {},
        timeRemaining: hasSectionalTimer && sections?.length > 0
          ? sections[0].timeRemainingSeconds || sections[0].timeLimit * 60
          : (duration || 60) * 60, // Convert minutes to seconds, default to 60 min
        // Internal choice groups
        choiceGroups: choiceGroups || [],
        // TEST_PREP fields
        hasSectionalTimer: hasSectionalTimer || false,
        sectionSwitchingAllowed: sectionSwitchingAllowed !== false,
        currentSectionIndex: currentSectionIndex || 0,
        sections: sections || [],
        sectionProgress: sectionProgress || []
      };

      // Initialize question states (with safety check)
      state.questionStates = {};
      if (validQuestions && validQuestions.length > 0) {
        for (const q of validQuestions) {
          if (q && q.questionId) {
            state.questionStates[q.questionId] = {
              visited: false,
              markedForReview: false
            };
          }
        }
      }

      state.loading.startAttempt = false;
      state.flow.step = 'taking_quiz';
      state.flow.isStarting = false;

    },

    // ============ Quiz Navigation ============
    setCurrentQuestion: (state, action) => {
      const newIndex = action.payload;
      const targetQuestion = state.attempt.questions[newIndex];
      if (!targetQuestion) return;

      // Block cross-section navigation when section switching is not allowed
      if (state.attempt.hasSectionalTimer && !state.attempt.sectionSwitchingAllowed) {
        const currentSection = state.attempt.sections[state.attempt.currentSectionIndex];
        if (targetQuestion.sectionCode && currentSection?.sectionCode &&
            targetQuestion.sectionCode !== currentSection.sectionCode) {
          return;
        }
      }
      state.attempt.currentQuestionIndex = newIndex;
    },
    nextQuestion: (state) => {
      if (state.attempt.currentQuestionIndex < state.attempt.questions.length - 1) {
        // Block crossing section boundary when section switching is not allowed
        if (state.attempt.hasSectionalTimer && !state.attempt.sectionSwitchingAllowed) {
          const currentSection = state.attempt.sections[state.attempt.currentSectionIndex];
          const nextQuestion = state.attempt.questions[state.attempt.currentQuestionIndex + 1];
          if (currentSection && nextQuestion && nextQuestion.sectionCode !== currentSection.sectionCode) {
            return; // Block: next question is in a different section
          }
        }
        state.attempt.currentQuestionIndex += 1;
      }
    },
    previousQuestion: (state) => {
      if (state.attempt.currentQuestionIndex > 0) {
        // Block crossing section boundary when section switching is not allowed
        if (state.attempt.hasSectionalTimer && !state.attempt.sectionSwitchingAllowed) {
          const currentSection = state.attempt.sections[state.attempt.currentSectionIndex];
          const prevQuestion = state.attempt.questions[state.attempt.currentQuestionIndex - 1];
          if (currentSection && prevQuestion && prevQuestion.sectionCode !== currentSection.sectionCode) {
            return; // Block: previous question is in a different section
          }
        }
        state.attempt.currentQuestionIndex -= 1;
      }
    },

    // ============ Answers ============
    setSaveAnswerLoading: (state, action) => {
      state.loading.saveAnswer = action.payload;
      state.flow.isSavingAnswer = action.payload;
    },
    setAnswer: (state, action) => {
      const { questionId, answerText } = action.payload;
      state.attempt.answers[questionId] = answerText;
      state.loading.saveAnswer = false;
      state.flow.isSavingAnswer = false;
      // NOTE: deliberately does NOT clear `unsavedAnswers`. Only the server
      // confirming the write may do that — see clearAnswerUnsaved.
    },
    /** The server did not accept this answer. Keep it visible, mark it unsaved. */
    markAnswerUnsaved: (state, action) => {
      const { questionId, answerText } = action.payload;
      state.attempt.unsavedAnswers[questionId] = answerText;
    },
    /** The server confirmed this answer. */
    clearAnswerUnsaved: (state, action) => {
      delete state.attempt.unsavedAnswers[action.payload.questionId];
    },

    // ============ Timer ============
    decrementTimer: (state) => {
      if (state.attempt.timeRemaining > 0) {
        state.attempt.timeRemaining -= 1;
      }
    },
    setTimeRemaining: (state, action) => {
      state.attempt.timeRemaining = action.payload;
    },

    // ============ Section Timer (TEST_PREP) ============
    decrementSectionTimer: (state, action) => {
      const sectionIndex = action.payload ?? state.attempt.currentSectionIndex;
      if (state.attempt.sections[sectionIndex]) {
        const section = state.attempt.sections[sectionIndex];
        if (section.timeRemainingSeconds > 0) {
          section.timeRemainingSeconds -= 1;
        }
      }
    },
    setSectionTimeRemaining: (state, action) => {
      const { sectionIndex, timeRemainingSeconds } = action.payload;
      if (state.attempt.sections[sectionIndex]) {
        state.attempt.sections[sectionIndex].timeRemainingSeconds = timeRemainingSeconds;
      }
    },

    // ============ Section Navigation (TEST_PREP) ============
    setCurrentSection: (state, action) => {
      const newSectionIndex = action.payload;
      if (newSectionIndex >= 0 && newSectionIndex < state.attempt.sections.length) {
        // Update active status
        state.attempt.sections.forEach((s, i) => {
          s.isActive = i === newSectionIndex;
        });
        state.attempt.currentSectionIndex = newSectionIndex;
        // Set timeRemaining to current section's time
        const currentSection = state.attempt.sections[newSectionIndex];
        if (currentSection) {
          state.attempt.timeRemaining = currentSection.timeRemainingSeconds;
        }
      }
    },
    lockSection: (state, action) => {
      const sectionIndex = action.payload;
      if (state.attempt.sections[sectionIndex]) {
        state.attempt.sections[sectionIndex].isLocked = true;
      }
    },
    updateSectionProgress: (state, action) => {
      state.attempt.sectionProgress = action.payload;
      // Also update sections array
      if (action.payload && state.attempt.sections) {
        for (const progress of action.payload) {
          const section = state.attempt.sections.find(s => s.sectionCode === progress.sectionCode);
          if (section) {
            section.isLocked = progress.isLocked;
            section.timeRemainingSeconds = progress.timeRemainingSeconds;
          }
        }
      }
    },

    // ============ Question States (TEST_PREP) ============
    setQuestionVisited: (state, action) => {
      const questionId = action.payload;
      if (!state.questionStates[questionId]) {
        state.questionStates[questionId] = { visited: false, markedForReview: false };
      }
      state.questionStates[questionId].visited = true;
    },
    setMarkedForReview: (state, action) => {
      const { questionId, isMarked } = action.payload;
      if (!state.questionStates[questionId]) {
        state.questionStates[questionId] = { visited: false, markedForReview: false };
      }
      state.questionStates[questionId].markedForReview = isMarked;
      state.questionStates[questionId].visited = true;
    },

    // ============ Submit Quiz ============
    setSubmitLoading: (state, action) => {
      state.loading.submitQuiz = action.payload;
      state.flow.isSubmitting = action.payload;
    },
    setQuizSubmitted: (state, action) => {
      const { attemptId, score, submittedAt, timeSpentSeconds, assessmentName, answers } = action.payload;
      state.result = {
        attemptId,
        assessmentName: assessmentName || state.attempt.assessmentName,
        participantName: `${state.participantInfo.firstName} ${state.participantInfo.lastName}`,
        score,
        status: 'SUBMITTED',
        submittedAt,
        timeSpentSeconds,
        answers: answers || [],
        showCorrectAnswers: !!answers
      };
      state.loading.submitQuiz = false;
      state.flow.step = 'result';
      state.flow.isSubmitting = false;
    },

    // ============ Load Result ============
    setLoadResultLoading: (state, action) => {
      state.loading.loadResult = action.payload;
    },
    setResult: (state, action) => {
      state.result = action.payload;
      state.loading.loadResult = false;
      state.flow.step = 'result';
    },

    // ============ Flow Control ============
    goToStep: (state, action) => {
      state.flow.step = action.payload;
    },
    resetToCodeEntry: (state) => {
      return {
        ...initialState,
        accessCode: state.accessCode
      };
    },

    // ============ Error Handling ============
    setError: (state, action) => {
      state.error = action.payload;
      // Reset loading states
      Object.keys(state.loading).forEach(key => {
        state.loading[key] = false;
      });
      state.flow.isStarting = false;
      state.flow.isSubmitting = false;
      state.flow.isSavingAnswer = false;
    },
    clearError: (state) => {
      state.error = null;
    },

    // ============ Proctoring ============
    setProctoringConfig: (state, action) => {
      // Accepts the `proctoring` block from the validated assessment config.
      const cfg = action.payload || {};
      state.proctoring.enabled = Boolean(cfg.enabled);
      state.proctoring.tabSwitch = Boolean(cfg.tabSwitch);
      state.proctoring.webcam = Boolean(cfg.webcam);
      state.proctoring.gaze = Boolean(cfg.gaze);
    },
    incrementProctoringLiveCounts: (state, action) => {
      // Optimistic UI bump from the hook BEFORE the server roundtrip so the
      // pill is responsive. Server-side counters are the source of truth
      // for the analytics drawer; this is display-only.
      const delta = action.payload || {};
      if (delta.tabSwitchCount) {
        state.proctoring.liveCounts.tabSwitchCount += delta.tabSwitchCount;
      }
      if (delta.screenSwitchCount) {
        state.proctoring.liveCounts.screenSwitchCount += delta.screenSwitchCount;
      }
      if (delta.blurCount) {
        state.proctoring.liveCounts.blurCount += delta.blurCount;
      }
      if (delta.totalAwayMs) {
        state.proctoring.liveCounts.totalAwayMs += delta.totalAwayMs;
      }
    },
    setProctoringFlushResult: (state, action) => {
      state.proctoring.lastFlush = action.payload || null;
    },
    resetProctoringState: (state) => {
      state.proctoring = {
        enabled: false,
        tabSwitch: false,
        webcam: false,
        gaze: false,
        liveCounts: { tabSwitchCount: 0, blurCount: 0, totalAwayMs: 0 },
        lastFlush: null
      };
    },

    // ============ Reset ============
    resetParticipantState: () => initialState
  }
});

// Export actions
export const {
  setAccessCode,
  setLinkCode,
  setValidateCodeLoading,
  setCodeValidated,
  setLinkCodeValidated,
  setCodeInvalid,
  setSearchLoading,
  setInstitutes,
  clearInstitutes,
  updateParticipantInfo,
  setParticipantInstituteType,
  setSelectedInstitute,
  setStartAttemptLoading,
  setAttemptStarted,
  setCurrentQuestion,
  nextQuestion,
  previousQuestion,
  setSaveAnswerLoading,
  setAnswer,
  markAnswerUnsaved,
  clearAnswerUnsaved,
  decrementTimer,
  setTimeRemaining,
  // Section timer actions (TEST_PREP)
  decrementSectionTimer,
  setSectionTimeRemaining,
  setCurrentSection,
  lockSection,
  updateSectionProgress,
  // Question state actions (TEST_PREP)
  setQuestionVisited,
  setMarkedForReview,
  setSubmitLoading,
  setQuizSubmitted,
  setLoadResultLoading,
  setResult,
  goToStep,
  resetToCodeEntry,
  setError,
  clearError,
  // Proctoring actions
  setProctoringConfig,
  incrementProctoringLiveCounts,
  setProctoringFlushResult,
  resetProctoringState,
  resetParticipantState
} = participantSlice.actions;

// ============ Selectors ============
export const selectAccessCode = (state) => state.participant.accessCode;
export const selectLinkCode = (state) => state.participant.linkCode;
export const selectIsValidCode = (state) => state.participant.isValidCode;
export const selectIsLinkCodeValidated = (state) => state.participant.isLinkCodeValidated;
export const selectAssessmentInfo = (state) => state.participant.assessmentInfo;
export const selectCreatorInstitute = (state) => state.participant.creatorInstitute;
export const selectInstitutes = (state) => state.participant.institutes;
export const selectParticipantInfo = (state) => state.participant.participantInfo;
export const selectAttempt = (state) => state.participant.attempt;
export const selectCurrentQuestion = (state) => {
  const { questions, currentQuestionIndex } = state.participant.attempt;
  return questions[currentQuestionIndex] || null;
};
export const selectAnswers = (state) => state.participant.attempt.answers;
export const selectTimeRemaining = (state) => state.participant.attempt.timeRemaining;
export const selectResult = (state) => state.participant.result;
export const selectFlow = (state) => state.participant.flow;
export const selectLoading = (state) => state.participant.loading;
export const selectError = (state) => state.participant.error;

// Derived selectors
export const selectAnsweredCount = (state) => {
  const answers = state.participant.attempt.answers;
  return Object.keys(answers).filter(key => answers[key] && answers[key].trim() !== '').length;
};

export const selectProgress = (state) => {
  const { questions, answers } = state.participant.attempt;
  const answeredCount = Object.keys(answers).filter(key => answers[key] && answers[key].trim() !== '').length;
  return questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;
};

// ============ TEST_PREP Selectors ============
export const selectHasSectionalTimer = (state) => state.participant.attempt.hasSectionalTimer;
export const selectSectionSwitchingAllowed = (state) => state.participant.attempt.sectionSwitchingAllowed;
export const selectCurrentSectionIndex = (state) => state.participant.attempt.currentSectionIndex;
export const selectSections = (state) => state.participant.attempt.sections;
export const selectSectionProgress = (state) => state.participant.attempt.sectionProgress;
export const selectQuestionStates = (state) => state.participant.questionStates;

export const selectCurrentSection = (state) => {
  const { sections, currentSectionIndex } = state.participant.attempt;
  return sections[currentSectionIndex] || null;
};

export const selectCurrentSectionQuestions = (state) => {
  const { questions, hasSectionalTimer, currentSectionIndex, sections } = state.participant.attempt;
  if (!hasSectionalTimer || !sections.length) {
    return questions;
  }
  const currentSection = sections[currentSectionIndex];
  if (!currentSection) return [];
  return questions.filter(q => q.sectionCode === currentSection.sectionCode);
};

export const selectChoiceGroups = (state) => state.participant.attempt.choiceGroups;

/**
 * Returns the choice group status for a given question, or null if no group applies.
 * { groupId, requiredCount, answeredCount, isGroupFull, isThisQuestionAnswered }
 */
export const selectChoiceGroupStatusForQuestion = (state, questionId) => {
  const { choiceGroups, answers } = state.participant.attempt;
  if (!choiceGroups?.length || !questionId) return null;

  const group = choiceGroups.find(g => g.questionIds?.includes(questionId));
  if (!group) return null;

  const answeredCount = group.questionIds.filter(
    qId => answers[qId] && answers[qId].trim() !== ''
  ).length;

  const isThisQuestionAnswered = !!(answers[questionId] && answers[questionId].trim() !== '');

  return {
    groupId: group.groupId,
    label: group.label,
    requiredCount: group.requiredCount,
    totalQuestions: group.totalQuestions,
    answeredCount,
    isGroupFull: answeredCount >= group.requiredCount,
    isThisQuestionAnswered,
  };
};

export const selectMarkedForReviewCount = (state) => {
  const questionStates = state.participant.questionStates;
  return Object.values(questionStates).filter(s => s.markedForReview).length;
};

export const selectVisitedCount = (state) => {
  const questionStates = state.participant.questionStates;
  return Object.values(questionStates).filter(s => s.visited).length;
};

// ============ Proctoring Selectors ============
export const selectProctoring = (state) => state.participant.proctoring;
export const selectProctoringEnabled = (state) =>
  Boolean(state.participant.proctoring?.enabled);
export const selectProctoringLiveCounts = (state) =>
  state.participant.proctoring?.liveCounts || { tabSwitchCount: 0, blurCount: 0, totalAwayMs: 0 };


/** Answers the server has not confirmed, as { questionId: answerText }. */
export const selectUnsavedAnswers = (state) => state.participant.attempt.unsavedAnswers || {};
/** True while any answer is still waiting on the server. Blocks submit. */
export const selectHasUnsavedAnswers = (state) =>
  Object.keys(state.participant.attempt.unsavedAnswers || {}).length > 0;

export default participantSlice.reducer;
